// Pure helpers for the "require a salesperson before placing a new order" guard.
// Kept free of React/redux so the option-shaping and the assigned-check can be
// unit tested in isolation.

const pickId = (sp) => sp?.UserId ?? sp?.salesPersonId;
const pickName = (sp) => sp?.UserName ?? sp?.salesPerson ?? '';
const hasId = (v) => v != null && v !== '' && v !== 0;

// Map a GetSalesPerson result list into react-select options { value, label }.
// Skips entries without an id; returns [] for non-arrays.
export const buildSalesPersonOptions = (list) => {
  if (!Array.isArray(list)) return [];
  return list
    .filter((sp) => sp && hasId(pickId(sp)))
    .map((sp) => ({ value: pickId(sp), label: pickName(sp) }));
};

// A salesperson is "assigned" when the loaded record carries a non-empty
// SalesPersonId, or the user has picked one in the select.
export const isSalesPersonAssigned = (orderInstallationDetails, selectedSalesPersonId) =>
  hasId(orderInstallationDetails?.SalesPersonId) || hasId(selectedSalesPersonId);

// Build the UpdateCustomerInfo (PUT) body from a GetCustomerInfo customer row,
// overriding the salesperson. The salesperson belongs to the contact info (not
// the order), so a newly-picked one is persisted here before the order is saved.
// Mirrors the exact field shape UpdateCustomerInfo expects; missing fields fall
// back to safe empties so a partial customer row can't blank out the record.
export const buildUpdateCustomerInfoPayload = (customer, salesPersonId, createdBy) => {
  const c = customer || {};
  let tmpContact = c.ilContactViewModel.map(item => ({
    approvalRank: item.approvalRank,
    approvalType: item.approvalType,
    email: item.email,
    ext: item.ext,
    name: item.name,
    notes: item.notes,
    number: item.number,
    roll: item.roll
  }));
  return {
    contactInfoId: c.contactInfoId,
    shipToPropertyName: c.shipToPropertyName ?? '',
    shipToManagementCompany: c.shipToManagementCompany ?? '',
    shipToAddress: c.shipToAddress ?? '',
    shipToCity: c.shipToCity ?? '',
    shipToState: c.shipToState ?? '',
    shipToZip: c.shipToZip ?? '',
    shipToPhone: c.shipToPhone ?? '',
    billToName: c.billToName ?? '',
    billToAddress: c.billToAddress ?? '',
    billToCity: c.billToCity ?? '',
    billToState: c.billToState ?? '',
    billToZip: c.billToZip ?? '',
    billToPhone: c.billToPhone ?? '',
    billToEmail: c.billToEmail ?? '',
    storeId: c.storeId,
    storeName: c.storeName ?? '',
    salesPersonId: salesPersonId ?? c.salesPersonId ?? null,
    ilContactViewModel: tmpContact,
    isContractPricingAdded: c.isContractPricingAdded ?? false,
    viableSeamsFloorPlans: Array.isArray(c.viableSeamsFloorPlans) ? c.viableSeamsFloorPlans : [],
    installerNotes: c.installerNotes ?? '',
    paymentTerms: c.paymentTerms ?? '',
    creditLimit: c.creditLimit ?? 0,
    costarId: c.costarId ?? '',
    requirePOToInstall: c.requirePOToInstall ?? false,
    createdBy: c.createdBy ?? createdBy,
  };
};
